import { useEffect, useRef, useState } from "react";
import { fetchFlightData } from "shared/services/flights/get-flight-data.service";
import { FlightDataRequestError } from "shared/services/flights/flight-data-request-error";
import { CreateSearchIntentResponse } from "../services/flights/createSearchIntentResponse";
import { API_URLS } from "shared/config/api/urlsConfig";
import { generateGUID } from "shared/utils/guid";
import { getEnvironment } from "../utils/getEnvironment";
import { captureSentryException } from "shared/services/sentry/captureException";
import { sendGTMEvent } from "shared/services/GTM";
import { getSearchEndedEventData } from "./searchEndedEventData";
import { sendTrackingWebhook } from "shared/services/trackingWebhook/send-tracking-webhook.service";
import type { GTMEventParams } from "shared/services/GTM/types";
import {
  FlightSearchIntent,
  getFlightSearchEventData,
} from "./flightSearchEventData";

type FlightSearchPayload = {
  requestId?: string;
  searchIntent?: FlightSearchIntent;
};

type SearchFlightsOptions = {
  isFirstSearchRequest?: boolean;
  isLastSearchRequest?: boolean;
  pollingNumber?: number;
};

/**
 * Resultado de uma chamada a `searchFlights`. A falha não é lançada: já foi
 * reportada e guardada em `error`; quem faz o polling usa isto para decidir se
 * continua.
 */
export type SearchFlightsResult = { ok: true } | { ok: false; error: Error };

const getGTMPlatformType = () => {
  if (typeof window === "undefined") {
    return "Voopter Metasearch (desktop)";
  }

  const isMobileDevice =
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      window.navigator.userAgent,
    ) || window.innerWidth < 768;

  return isMobileDevice
    ? "Voopter Metasearch (mobile)"
    : "Voopter Metasearch (desktop)";
};

const isSearchIntentFinished = (response: CreateSearchIntentResponse) => {
  return response.message.wsChannels.every((channel) => {
    const isChannelFinished = channel.status === "finished";
    const isCacheDataFinished = Object.values(channel.cacheData).every(
      (cache) => cache.status === "finished",
    );

    return isChannelFinished || isCacheDataFinished;
  });
};

/**
 * Hook para gerenciar a busca de voos.
 */
export const useFlightSearch = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<CreateSearchIntentResponse | null>(null);
  const searchRequestIdRef = useRef<string | null>(null);
  const searchEndGTMRequestIdRef = useRef<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(getFlightSearchUrl());
  }, []);

  const getFlightSearchUrl = () => {
    if (typeof window === "undefined") {
      return API_URLS.flightSearch;
    }

    const environment = getEnvironment();

    if (environment === "development") {
      return API_URLS.flightSearch;
    }

    if (environment === "staging" || environment === "homolog") {
      return API_URLS.flightSearch_dev;
    }

    return API_URLS.flightSearch;
  };

  const searchFlights = async (
    payload: FlightSearchPayload,
    options?: SearchFlightsOptions,
  ): Promise<SearchFlightsResult> => {
    setLoading(true);
    setError(null);

    try {
      const searchIntent = payload.searchIntent;
      const pollingNumber = options?.pollingNumber ?? 0;

      if (options?.isFirstSearchRequest) {
        searchRequestIdRef.current = generateGUID();
        searchEndGTMRequestIdRef.current = null;

        // One GA4 `search` per search cycle, sent before the first
        // createSearchIntent, so direct visits to the SERP are counted too.
        if (searchIntent) {
          try {
            const flightSearchEventData =
              getFlightSearchEventData(searchIntent);
            sendGTMEvent("flightSearch", flightSearchEventData);
            sendTrackingWebhook("search", flightSearchEventData);
          } catch (gtmError) {
            captureSentryException(gtmError, {
              tags: {
                feature: "flight-search",
                operation: "sendFlightSearchGTM",
              },
            });
          }
        }
      }

      const requestId = searchRequestIdRef.current;
      const payloadWithRequestId = requestId
        ? {
            ...payload,
            requestId,
          }
        : payload;

      const flightData = await fetchFlightData(
        url ?? getFlightSearchUrl(),
        payloadWithRequestId,
      );

      if (
        requestId &&
        (options?.isLastSearchRequest || isSearchIntentFinished(flightData))
      ) {
        const searchEndedEventData = getSearchEndedEventData(flightData);

        if (searchEndGTMRequestIdRef.current !== requestId) {
          try {
            const searchEndEventData: GTMEventParams["search-end"] = {
              origin: searchIntent?.originAirport ?? "",
              destination: searchIntent?.destinationAirport ?? "",
              adults: searchIntent?.quantityOfAdults ?? 0,
              childs: searchIntent?.quantityOfChildrens ?? 0,
              babies: searchIntent?.quantityOfBabies ?? 0,
              departureDates: searchIntent?.departureDate ?? [],
              returnDates: searchIntent?.returnDate ?? [],
              platform: getGTMPlatformType(),
              results: searchEndedEventData.gtmResults,
            };
            sendGTMEvent("search-end", searchEndEventData);
            sendTrackingWebhook("searchEnd", {
              event: `search-end-${getEnvironment()}`,
              ...searchEndEventData,
            });
            searchEndGTMRequestIdRef.current = requestId;
          } catch (gtmError) {
            captureSentryException(gtmError, {
              tags: {
                feature: "flight-search",
                operation: "sendSearchEndGTM",
              },
              contexts: {
                flightSearch: {
                  requestId,
                  pollingNumber,
                },
              },
            });
            console.error("Erro ao enviar evento search-end ao GTM", gtmError);
          }
        }
      }

      setData(flightData);
      return { ok: true };
    } catch (err) {
      // fetchFlightData já reportou a falha da requisição com URL e status.
      if (!(err instanceof FlightDataRequestError)) {
        captureSentryException(err, {
          tags: {
            feature: "flight-search",
            operation: "searchFlights",
          },
          contexts: {
            flightSearch: {
              requestId: searchRequestIdRef.current,
              endpoint: url ?? getFlightSearchUrl(),
            },
          },
        });
      }
      const failure = err instanceof Error ? err : new Error("Erro inesperado");
      setError(failure);
      return { ok: false, error: failure };
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, data, searchFlights };
};
